/**
 * Farm & Acre Co - Allflex Marking Book print picker (SAP rebuild, 21 Oct 2026)
 *
 * Spec: Plans/allflex-transition-2026-10/sap-rebuild/08-theme-changes.md sections 1 to 3,
 * and PLAN.md "New Allflex print picker". Markup: snippets/allflex-marking-picker.liquid,
 * which only renders when allflex_mb_active (vendor Allflex AND variant SKU is a book SKU),
 * so this file is never loaded on FOFIA, Leader, Shearwell or non-book Allflex pages.
 *
 * Flow: lines -> layout cards -> one box per line -> validation -> gate.
 *   - Reads assets/allflex-marking-book.json (URL from data-book-url), looks up the current
 *     variant SKU, offers only the line counts that book page has.
 *   - Writes the saved properties into the snippet's inputs: Print Style, Print Lines (fee
 *     token), Line 1..5, and for sets with a printed male Male Print Style, Male Print Lines,
 *     Male Line 1..5. Custom layout: Custom Print + Print Type = Complex Print.
 *   - Fills the mb-layout gate only when a layout is picked and every filled line fits.
 *   - Fails closed (message, empty gate) if the JSON can't be fetched, is invalid, or the SKU
 *     isn't in it. Never falls back to the old program list.
 *
 * Print fees are NOT calculated here. fee-auto-add.js charges LINE_FEES[Print Lines] and
 * LINE_FEES[Male Print Lines] per tag; dynamic-pricing.js shows the same in the price box.
 *
 * Vanilla ES2017, no dependencies. Under Node (`require`) it exports the pure core only,
 * for Plans/allflex-transition-2026-10/sap-rebuild/stage-d/theme-tests/.
 */
(function (root, factory) {
  'use strict';
  var core = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = core;
    return;
  }
  root.AllflexMarkingPicker = core;
  if (typeof document !== 'undefined') core.boot(document);
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ===========================================================================
  // PURE CORE (no DOM)
  // ===========================================================================

  var METHOD_LABEL = { LS: 'Laser', FX: 'LazaFX' };
  var CONTENT_LABEL = { FT: 'Text', NR: 'Numbers' };
  var FEE_TOKEN = { 1: '1-line', 2: '2-lines', 3: '3-lines' };
  var MAX_LINES = 5;
  var IMAGE_RE = /^allflex-mb-[A-Za-z0-9]+\.png$/;
  var CUSTOM_STYLE = 'Custom Layout';

  function has(obj, key) {
    return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
  }

  /** Fee token for a printed tag with n lines: same values the old print-style select wrote. */
  function feeToken(n) {
    n = Number(n) || 0;
    if (n <= 0) return 'blank';
    return FEE_TOKEN[n] || 'custom'; // 4 and 5 lines -> 'custom' ($1.21 tier), as before
  }

  function validLine(l) {
    return !!l && typeof l === 'object' &&
      Number(l.line) >= 1 && Number(l.line) <= MAX_LINES &&
      Number(l.maxChars) > 0 &&
      has(METHOD_LABEL, l.method) && has(CONTENT_LABEL, l.content);
  }

  function validProgram(p) {
    if (!p || typeof p !== 'object' || typeof p.code !== 'string' || !p.code) return false;
    if (!Array.isArray(p.female) || !p.female.length || p.female.length > MAX_LINES) return false;
    if (!p.female.every(validLine)) return false;
    if (p.male != null) {
      if (!Array.isArray(p.male) || !p.male.length || p.male.length > MAX_LINES || !p.male.every(validLine)) return false;
    }
    var lc = Number(p.lineCount);
    if (!(lc >= 1 && lc <= MAX_LINES)) return false;
    if (p.image != null && !IMAGE_RE.test(String(p.image))) return false;
    return true;
  }

  /** Shape check of the whole book. Anything off = fail closed. */
  function isValidBook(book) {
    if (!book || typeof book !== 'object' || !book.pages || !book.skus) return false;
    if (typeof book.pages !== 'object' || typeof book.skus !== 'object') return false;
    var skus = Object.keys(book.skus);
    if (!skus.length) return false;
    for (var i = 0; i < skus.length; i++) {
      var page = book.pages[String(book.skus[skus[i]])];
      if (!page || !Array.isArray(page.programs) || !page.programs.length) return false;
    }
    var pageNos = Object.keys(book.pages);
    for (var j = 0; j < pageNos.length; j++) {
      var pg = book.pages[pageNos[j]];
      if (!pg || !Array.isArray(pg.programs) || !pg.programs.every(validProgram)) return false;
    }
    return true;
  }

  /** SKU (= Allflex UIN) -> { sku, pageNo, page } or null. */
  function lookup(book, sku) {
    var key = sku == null ? '' : String(sku).trim();
    if (!key || !book || !has(book.skus, key)) return null;
    var pageNo = String(book.skus[key]);
    var page = book.pages && book.pages[pageNo];
    if (!page || !Array.isArray(page.programs) || !page.programs.length) return null;
    return { sku: key, pageNo: pageNo, page: page };
  }

  /** The line counts this book page actually offers, ascending. */
  function lineCounts(page) {
    var seen = {};
    var out = [];
    (page && page.programs || []).forEach(function (p) {
      var n = Number(p.lineCount);
      if (!seen[n]) { seen[n] = true; out.push(n); }
    });
    return out.sort(function (a, b) { return a - b; });
  }

  function programsFor(page, n) {
    return (page && page.programs || []).filter(function (p) { return Number(p.lineCount) === Number(n); });
  }

  /** The layout to pick for the customer when n lines has only one (2 lines on Large = IG5), else ''. */
  function autoCode(page, n) {
    var list = programsFor(page, n);
    return list.length === 1 ? list[0].code : '';
  }

  function findProgram(page, code) {
    var list = page && page.programs || [];
    for (var i = 0; i < list.length; i++) if (list[i].code === code) return list[i];
    return null;
  }

  function hasPrintedMale(program) {
    return !!program && Array.isArray(program.male) && program.male.length > 0;
  }

  /** Printed tags per set/tag for this program: 2 when the male is printed, else 1. */
  function printedTagCount(program) {
    return hasPrintedMale(program) ? 2 : 1;
  }

  /** 'pair' = female + printed male, 'button' = female + blank button male, 'single' = one-piece. */
  function pageKind(page) {
    var progs = page && page.programs || [];
    if (progs.length && progs.every(hasPrintedMale)) return 'pair';
    if (progs.length && progs.every(function (p) { return !!p.maleButton; })) return 'button';
    return 'single';
  }

  /**
   * Custom Layout is off (Aaron, 21 Oct): Allflex sells custom designs as their own UINs (899022 etc.), not as a
   * printed pair, so customers order them on the Allflex Custom Designed Tags product. The snippet links there.
   */
  function customAllowed() {
    return false;
  }

  /**
   * Split a set code into the female and male halves (Male Print Style = male half).
   * Small sets join the halves with "00" (S500S5 -> S5 / S5). Otherwise the female half is
   * 3 characters for the "I" codes (IA6IL5 -> IA6 / IL5, IZRLP -> IZR / LP) and 2 for the
   * rest (GPLP -> GP / LP, DMMM -> DM / MM, TLBL -> TL / BL, ATXT -> AT / XT).
   */
  function splitCode(code, maleIsPrinted) {
    code = String(code || '');
    if (!maleIsPrinted) return { female: code, male: '' };
    var zz = code.indexOf('00');
    if (zz > 0) return { female: code.slice(0, zz), male: code.slice(zz + 2) };
    var n = code.charAt(0) === 'I' ? 3 : 2;
    return { female: code.slice(0, n), male: code.slice(n) };
  }

  function isNumberLine(spec, allText) {
    return !!spec && spec.content === 'NR' && !allText;
  }

  /** Card text, per 08: "L1: 9mm | 12 chr | Laser | Text". */
  function cardLineText(spec) {
    return 'L' + spec.line + ': ' + spec.heightMm + 'mm | ' + spec.maxChars + ' chr | ' +
      METHOD_LABEL[spec.method] + ' | ' + CONTENT_LABEL[spec.content];
  }

  /** Short spec carried on the saved Line input (tag-order-summary.js prints it in Print Details). */
  function dataSpec(spec, allText) {
    return spec.heightMm + 'mm ' + METHOD_LABEL[spec.method] + ' ' + (isNumberLine(spec, allText) ? 'numbers' : 'text');
  }

  /** Customer-facing box hint: "19mm, up to 3 characters, Laser, text". */
  function boxHint(spec, allText) {
    var num = isNumberLine(spec, allText);
    return spec.heightMm + 'mm, up to ' + spec.maxChars + (num ? ' digits' : ' characters') + ', ' +
      METHOD_LABEL[spec.method] + ', ' + (num ? 'numbers' : 'text');
  }

  function digitsOnly(s) {
    return String(s == null ? '' : s).replace(/\D+/g, '');
  }

  /**
   * How many tags a female number range makes (Aaron, 23 Oct: quantity follows the numbers).
   * First female number line with a start and an end, end >= start: end - start + 1. Else 0.
   * The male's numbers never set the quantity: quantity counts sets.
   */
  function rangeQuantity(program, female, allText) {
    var specs = program && program.female || [];
    for (var i = 0; i < specs.length; i++) {
      if (!isNumberLine(specs[i], allText)) continue;
      var e = female && female[specs[i].line] || {};
      var s = digitsOnly(e.start), t = digitsOnly(e.end);
      if (!s || !t || s !== String(e.start || '') || t !== String(e.end || '')) continue;
      var a = parseInt(s, 10), b = parseInt(t, 10);
      if (b >= a) return b - a + 1;
    }
    return 0;
  }

  /**
   * Check one line. entry = { text } for text lines, { start, end } for number lines.
   * Returns { ok, empty, value, error }. value is what gets saved ("1001" or "1001 to 1200").
   */
  function checkLine(spec, entry, allText, label) {
    entry = entry || {};
    var max = Number(spec.maxChars);
    if (!isNumberLine(spec, allText)) {
      var t = String(entry.text == null ? '' : entry.text).trim();
      if (!t) return { ok: true, empty: true, value: '' };
      if (t.length > max) return { ok: false, empty: false, value: t, error: label + ': ' + max + ' characters max' };
      return { ok: true, empty: false, value: t };
    }
    var s = String(entry.start == null ? '' : entry.start).trim();
    var e = String(entry.end == null ? '' : entry.end).trim();
    if (!s && !e) return { ok: true, empty: true, value: '' };
    var shown = e ? s + ' to ' + e : s;
    if (!s) return { ok: false, empty: false, value: shown, error: label + ': put the start number in first' };
    if (!/^\d+$/.test(s) || (e && !/^\d+$/.test(e))) return { ok: false, empty: false, value: shown, error: label + ': numbers only' };
    if (s.length > max || e.length > max) return { ok: false, empty: false, value: shown, error: label + ': ' + max + ' digits max' };
    if (e && Number(e) < Number(s)) return { ok: false, empty: false, value: shown, error: label + ': the end number needs to be bigger than the start' };
    return { ok: true, empty: false, value: shown };
  }

  function emptyProps() {
    var p = {
      'Print Style': '', 'Print Lines': '', 'Male Print Style': '', 'Male Print Lines': '',
      'Print Type': '', 'Custom Print': ''
    };
    for (var i = 1; i <= MAX_LINES; i++) { p['Line ' + i] = ''; p['Male Line ' + i] = ''; }
    return p;
  }

  /**
   * Work out everything the order saves, plus the gate value.
   * input: { page, program, custom, customText, female, male, allText, maleTagNeeded, maleTagChosen }
   * female / male: { <lineNo>: { text, start, end } }
   */
  function evaluate(input) {
    input = input || {};
    var props = emptyProps();
    var specs = {};
    var lineErrors = {};
    var errors = [];
    var res = { valid: false, gate: '', props: props, specs: specs, lineErrors: lineErrors, errors: errors, filled: 0, printedTags: 0 };

    if (input.maleTagNeeded && !input.maleTagChosen) { errors.push('Pick your male tag'); return res; }
    if (!input.page) { errors.push('Print layouts did not load'); return res; }

    if (input.custom) {
      if (!customAllowed(input.page)) { errors.push('Custom layout is not offered on this tag'); return res; }
      var pair = pageKind(input.page) === 'pair';
      var text = String(input.customText == null ? '' : input.customText).trim();
      props['Print Style'] = CUSTOM_STYLE;
      props['Print Lines'] = 'custom';
      if (pair) props['Male Print Lines'] = 'custom';
      props['Print Type'] = 'Complex Print';
      props['Custom Print'] = text;
      res.printedTags = pair ? 2 : 1;
      if (!text) errors.push('Describe your custom design');
      res.valid = errors.length === 0;
      res.gate = res.valid ? CUSTOM_STYLE : '';
      return res;
    }

    var program = input.program;
    if (!program) { errors.push('Pick a layout'); return res; }

    var maleOn = hasPrintedMale(program);
    var token = feeToken(program.feeLineCount || program.lineCount);
    props['Print Style'] = program.code;
    props['Print Lines'] = token;
    if (maleOn) {
      props['Male Print Style'] = splitCode(program.code, true).male;
      props['Male Print Lines'] = token;
    }
    res.printedTags = printedTagCount(program);

    var allText = !!(input.allText && program.allTextOption);
    var female = input.female || {};
    var male = input.male || {};

    program.female.forEach(function (spec) {
      var key = 'Line ' + spec.line;
      var r = checkLine(spec, female[spec.line], allText, maleOn ? 'Female line ' + spec.line : 'Line ' + spec.line);
      specs[key] = dataSpec(spec, allText);
      if (!r.ok) { lineErrors[key] = r.error; errors.push(r.error); }
      else if (!r.empty) { props[key] = r.value; res.filled++; }
    });
    if (maleOn) {
      program.male.forEach(function (spec) {
        var key = 'Male Line ' + spec.line;
        var r = checkLine(spec, male[spec.line], false, 'Male line ' + spec.line);
        specs[key] = dataSpec(spec, false);
        if (!r.ok) { lineErrors[key] = r.error; errors.push(r.error); }
        else if (!r.empty) { props[key] = r.value; res.filled++; }
      });
    }
    if (res.filled === 0) errors.push('Type the wording for at least one line');

    res.valid = errors.length === 0;
    res.gate = res.valid ? program.code : '';
    return res;
  }

  /**
   * "Copy female wording" for sets with a printed male: copies line N to male line N where
   * both lines are the same kind and the wording fits the male line. Returns the new male
   * entries and the line numbers that could not be copied.
   */
  function copyFemaleToMale(program, female, allText) {
    var out = {};
    var skipped = [];
    if (!hasPrintedMale(program)) return { male: out, skipped: skipped };
    female = female || {};
    program.male.forEach(function (mSpec) {
      var fSpec = null;
      for (var i = 0; i < program.female.length; i++) if (program.female[i].line === mSpec.line) fSpec = program.female[i];
      var f = female[mSpec.line];
      if (!fSpec || !f) return;
      var fNum = isNumberLine(fSpec, allText);
      var mNum = isNumberLine(mSpec, false);
      var src = checkLine(fSpec, f, allText, 'x');
      if (src.empty) return;
      if (fNum !== mNum) { skipped.push(mSpec.line); return; }
      var entry = fNum ? { start: f.start || '', end: f.end || '' } : { text: String(f.text || '').trim() };
      if (!checkLine(mSpec, entry, false, 'x').ok) { skipped.push(mSpec.line); return; }
      out[mSpec.line] = entry;
    });
    return { male: out, skipped: skipped };
  }

  // ===========================================================================
  // DOM CONTROLLER (browser only)
  // ===========================================================================

  var bookCache = {};
  var variantData = null;

  function fetchBook(url) {
    if (bookCache[url]) return bookCache[url];
    bookCache[url] = new Promise(function (resolve, reject) {
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = setTimeout(function () { if (ctrl) ctrl.abort(); reject(new Error('timeout')); }, 15000);
      fetch(url, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (json) { clearTimeout(timer); resolve(json); },
              function (err) { clearTimeout(timer); reject(err); });
    });
    return bookCache[url];
  }

  function skuForVariantId(id) {
    if (!id) return null;
    if (variantData === null) {
      variantData = [];
      var el = document.getElementById('AllflexVariantData');
      if (el) {
        try { var parsed = JSON.parse(el.textContent); if (Array.isArray(parsed)) variantData = parsed; } catch (e) { variantData = []; }
      }
    }
    for (var i = 0; i < variantData.length; i++) {
      if (String(variantData[i].id) === String(id)) return variantData[i].sku == null ? '' : String(variantData[i].sku);
    }
    return null;
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  function fire(node, type) {
    if (!node) return;
    var ev;
    try { ev = new Event(type, { bubbles: true }); } catch (e) { ev = document.createEvent('Event'); ev.initEvent(type, true, false); }
    node.dispatchEvent(ev);
  }

  function freshSelection() {
    return { lineCount: 0, code: '', custom: false, allText: false, female: {}, male: {} };
  }

  var uid = 0;

  function Picker(root) {
    this.root = root;
    this.id = 'mb' + (++uid);
    this.formId = root.getAttribute('data-form-id') || '';
    this.assetBase = root.getAttribute('data-asset-base') || '';
    this.fees = {
      '1-line': root.getAttribute('data-fee-1-line') || '',
      '2-lines': root.getAttribute('data-fee-2-lines') || '',
      '3-lines': root.getAttribute('data-fee-3-lines') || '',
      'custom': root.getAttribute('data-fee-custom') || ''
    };
    this.setupFee = root.getAttribute('data-setup-fee') || '';
    var q = function (s) { return root.querySelector(s); };
    this.els = {
      status: q('[data-mb-status]'), fail: q('[data-mb-fail]'), body: q('[data-mb-body]'),
      feeNote: q('[data-mb-fee-note]'),
      lines: q('[data-mb-lines]'), cardsStep: q('[data-mb-cards-step]'), cards: q('[data-mb-cards]'),
      customStep: q('[data-mb-custom-step]'), customText: q('[data-mb-custom-text]'),
      boxesStep: q('[data-mb-boxes-step]'), boxes: q('[data-mb-boxes]'),
      summary: q('[data-mb-summary]'), problems: q('[data-mb-problems]'),
      gate: q('[data-allflex-field="mb-layout"]')
    };
    this.out = {};
    var self = this;
    Array.prototype.forEach.call(root.querySelectorAll('[data-mb-out]'), function (node) {
      self.out[node.getAttribute('data-mb-out')] = node;
    });
    this.maleRadios = Array.prototype.slice.call(root.querySelectorAll('[data-mb-male-tag]'));
    this.book = null;
    this.fetchFailed = false;
    this.lookup = null;
    this.sku = undefined;
    this.sel = freshSelection();
    this.lineInputs = {};
  }

  Picker.prototype.init = function () {
    var self = this;
    this.writeOutputs(null); // never trust values a browser restored into the outputs
    this.showStatus('Loading print layouts.');

    var schedule = function () { setTimeout(function () { self.sync(); }, 0); };
    this.maleRadios.forEach(function (r) { r.addEventListener('change', schedule); });
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('[data-variant-option]') && self.maleRadios.indexOf(t) === -1) schedule();
    });
    if (this.els.customText) {
      this.els.customText.addEventListener('input', function () { self.update(); });
    }
    var onQty = function (e) { if (e.target && e.target === self.qtyInput()) self.updateRangeNote(); };
    document.addEventListener('input', onQty);
    document.addEventListener('change', onQty);

    var url = this.root.getAttribute('data-book-url');
    if (!url || typeof fetch !== 'function') { this.failClosed(true); return; }
    fetchBook(url).then(function (book) {
      if (!isValidBook(book)) { self.failClosed(true); return; }
      self.book = book;
      self.annotateMaleCards();
      self.sync();
    }, function () { self.failClosed(true); });
  };

  /** The SKU the order will carry right now; null when a Male tag still has to be picked. */
  Picker.prototype.currentSku = function () {
    if (this.maleRadios.length) {
      var checked = this.maleRadios.filter(function (r) { return r.checked; })[0];
      if (!checked) return null;
      var direct = checked.getAttribute('data-sku');
      if (direct) return direct;
      var viaId = skuForVariantId(checked.getAttribute('data-variant-id'));
      return viaId == null ? '' : viaId;
    }
    var form = this.formId ? document.getElementById(this.formId) : null;
    var idInput = form ? form.querySelector('input[name="id"]') : null;
    var fromId = idInput ? skuForVariantId(idInput.value) : null;
    return fromId != null ? fromId : (this.root.getAttribute('data-sku') || '');
  };

  Picker.prototype.sync = function () {
    if (this.fetchFailed || !this.book) return;
    var sku = this.currentSku();
    if (sku === null) {
      this.lookup = null;
      this.sku = null;
      this.sel = freshSelection();
      this.writeOutputs(null);
      this.showStatus('Pick your male tag above and the print layouts will show here.');
      return;
    }
    if (this.lookup && sku === this.sku) return;
    var lk = lookup(this.book, sku);
    this.sku = sku;
    if (!lk) { this.lookup = null; this.failClosed(false); return; }
    var pageChanged = !this.lookup || this.lookup.pageNo !== lk.pageNo;
    this.lookup = lk;
    if (pageChanged) this.sel = freshSelection();
    var counts = lineCounts(lk.page);
    if (!this.sel.lineCount && !this.sel.custom && counts.length === 1 && !customAllowed(lk.page)) {
      this.sel.lineCount = counts[0];
    }
    if (this.sel.lineCount && !this.sel.code && !this.sel.custom) this.sel.code = autoCode(lk.page, this.sel.lineCount);
    this.showReady();
    this.renderAll();
  };

  Picker.prototype.showStatus = function (text) {
    if (this.els.fail) this.els.fail.hidden = true;
    if (this.els.body) this.els.body.hidden = true;
    if (this.els.status) { this.els.status.textContent = text; this.els.status.hidden = false; }
  };

  Picker.prototype.showReady = function () {
    if (this.els.fail) this.els.fail.hidden = true;
    if (this.els.status) this.els.status.hidden = true;
    if (this.els.body) this.els.body.hidden = false;
  };

  /** Fail closed: message on, picker off, every output and the gate empty. */
  Picker.prototype.failClosed = function (permanent) {
    if (permanent) this.fetchFailed = true;
    this.sel = freshSelection();
    this.writeOutputs(null);
    if (this.els.status) this.els.status.hidden = true;
    if (this.els.body) this.els.body.hidden = true;
    if (this.els.fail) this.els.fail.hidden = false;
  };

  Picker.prototype.annotateMaleCards = function () {
    var book = this.book;
    this.maleRadios.forEach(function (r) {
      var card = r.closest('.print-type-card');
      var desc = card ? card.querySelector('[data-mb-male-desc]') : null;
      if (!desc) return;
      var lk = lookup(book, r.getAttribute('data-sku') || skuForVariantId(r.getAttribute('data-variant-id')));
      var kind = lk ? pageKind(lk.page) : '';
      desc.textContent = kind === 'button'
        ? 'Button male comes blank. Print fee on the female only.'
        : kind === 'pair' ? 'Male printed too. Print fee on both tags.' : '';
    });
  };

  Picker.prototype.program = function () {
    return this.lookup && this.sel.code ? findProgram(this.lookup.page, this.sel.code) : null;
  };

  Picker.prototype.renderAll = function () {
    this.renderFeeNote();
    this.renderLines();
    this.renderCards();
    this.renderCustom();
    this.renderBoxes();
    this.update();
  };

  Picker.prototype.renderFeeNote = function () {
    var note = this.els.feeNote;
    if (!note) return;
    var kind = pageKind(this.lookup.page);
    note.textContent = kind === 'button'
      ? 'The button male comes blank, so the print fee is charged on the female tag only.'
      : kind === 'pair' ? 'Both tags in this set are printed, so the print fee is charged on each tag.' : '';
    note.hidden = !note.textContent;
  };

  Picker.prototype.optionButton = function (selected, title, sub) {
    var b = el('button', 'allflex-mb-picker__line-btn' + (selected ? ' is-selected' : ''));
    b.type = 'button';
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', selected ? 'true' : 'false');
    b.appendChild(el('span', 'allflex-mb-picker__line-btn-title', title));
    if (sub) b.appendChild(el('span', 'allflex-mb-picker__line-btn-fee', sub));
    return b;
  };

  Picker.prototype.renderLines = function () {
    var self = this;
    var wrap = this.els.lines;
    if (!wrap) return;
    wrap.textContent = '';
    lineCounts(this.lookup.page).forEach(function (n) {
      var fee = self.fees[feeToken(n)];
      var b = self.optionButton(!self.sel.custom && self.sel.lineCount === n,
        n + (n === 1 ? ' line' : ' lines'), fee ? '+' + fee + ' a tag' : '');
      b.addEventListener('click', function () {
        if (!self.sel.custom && self.sel.lineCount === n) return;
        self.sel.custom = false;
        self.sel.lineCount = n;
        self.sel.code = autoCode(self.lookup.page, n);
        self.renderAll();
      });
      wrap.appendChild(b);
    });
    if (customAllowed(this.lookup.page)) {
      var parts = [];
      if (this.fees.custom) parts.push('+' + this.fees.custom + ' a tag');
      if (this.setupFee) parts.push(this.setupFee + ' set-up');
      var c = this.optionButton(this.sel.custom, 'Custom layout', parts.length ? parts.join(', ') : 'Your own design');
      c.addEventListener('click', function () {
        if (self.sel.custom) return;
        self.sel.custom = true;
        self.sel.lineCount = 0;
        self.sel.code = '';
        self.renderAll();
      });
      wrap.appendChild(c);
    }
  };

  Picker.prototype.renderCards = function () {
    var self = this;
    var step = this.els.cardsStep;
    var wrap = this.els.cards;
    if (!step || !wrap) return;
    wrap.textContent = '';
    if (this.sel.custom || !this.sel.lineCount) { step.hidden = true; return; }
    step.hidden = false;
    var kind = pageKind(this.lookup.page);
    programsFor(this.lookup.page, this.sel.lineCount).forEach(function (p) {
      var selected = self.sel.code === p.code;
      var card = el('button', 'allflex-mb-picker__card' + (selected ? ' is-selected' : ''));
      card.type = 'button';
      card.setAttribute('role', 'radio');
      card.setAttribute('aria-checked', selected ? 'true' : 'false');
      card.setAttribute('aria-label', 'Layout ' + p.code);
      if (p.image && IMAGE_RE.test(p.image) && self.assetBase) {
        var img = el('img', 'allflex-mb-picker__card-img');
        img.src = self.assetBase + p.image;
        img.alt = 'Allflex print layout ' + p.code;
        img.loading = 'lazy';
        img.decoding = 'async';
        card.appendChild(img);
      }
      card.appendChild(el('span', 'allflex-mb-picker__card-code', p.code));
      var specs = el('span', 'allflex-mb-picker__card-specs');
      var fCol = el('span', 'allflex-mb-picker__spec-col');
      fCol.appendChild(el('span', 'allflex-mb-picker__spec-heading', kind === 'single' ? 'Tag' : 'Female'));
      p.female.forEach(function (l) { fCol.appendChild(el('span', 'allflex-mb-picker__spec-line', cardLineText(l))); });
      specs.appendChild(fCol);
      if (hasPrintedMale(p)) {
        var mCol = el('span', 'allflex-mb-picker__spec-col');
        mCol.appendChild(el('span', 'allflex-mb-picker__spec-heading', 'Male'));
        p.male.forEach(function (l) { mCol.appendChild(el('span', 'allflex-mb-picker__spec-line', cardLineText(l))); });
        specs.appendChild(mCol);
      } else if (p.maleButton) {
        var bCol = el('span', 'allflex-mb-picker__spec-col');
        bCol.appendChild(el('span', 'allflex-mb-picker__spec-heading', 'Male'));
        bCol.appendChild(el('span', 'allflex-mb-picker__spec-line', 'Button supplied blank'));
        specs.appendChild(bCol);
      }
      card.appendChild(specs);
      if (p.allTextOption) card.appendChild(el('span', 'allflex-mb-picker__card-note', 'Also comes with every line as text.'));
      card.addEventListener('click', function () {
        if (self.sel.code === p.code) return;
        self.sel.code = p.code;
        if (!p.allTextOption) self.sel.allText = false;
        self.renderCards();
        self.renderBoxes();
        self.update();
        var boxes = self.els.boxesStep;
        if (boxes && boxes.scrollIntoView) boxes.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      wrap.appendChild(card);
    });
  };

  Picker.prototype.renderCustom = function () {
    var step = this.els.customStep;
    var ta = this.els.customText;
    if (!step) return;
    step.hidden = !this.sel.custom;
    if (ta) ta.disabled = !this.sel.custom;
  };

  Picker.prototype.renderBoxes = function () {
    var self = this;
    var step = this.els.boxesStep;
    var wrap = this.els.boxes;
    this.lineInputs = {};
    this.rangeNote = null;
    if (!step || !wrap) return;
    wrap.textContent = '';
    var program = this.program();
    if (this.sel.custom) { step.hidden = true; return; }
    step.hidden = false;
    if (!program) {
      // Keep the step on screen so customers know where the wording goes (Brooke's test, 23 Oct).
      wrap.appendChild(el('p', 'allflex-mb-picker__boxes-wait', this.sel.lineCount
        ? 'Tap a layout above and your Line 1, Line 2 boxes show here.'
        : 'Pick how many lines, then a layout, and your Line 1, Line 2 boxes show here.'));
      return;
    }
    var maleOn = hasPrintedMale(program);
    var kind = pageKind(this.lookup.page);

    if (program.allTextOption) {
      var at = el('label', 'allflex-mb-picker__alltext');
      var cb = el('input');
      cb.type = 'checkbox';
      cb.checked = !!this.sel.allText;
      cb.addEventListener('change', function () {
        self.sel.allText = cb.checked;
        self.renderBoxes();
        self.update();
      });
      at.appendChild(cb);
      at.appendChild(el('span', null, ' Print text on every line instead of numbers'));
      wrap.appendChild(at);
    }

    wrap.appendChild(this.tagBlock(kind === 'single' ? '' : 'Female tag', program.female, this.sel.female, 'Line ', !!this.sel.allText));
    this.rangeNote = el('p', 'allflex-field__hint allflex-mb-picker__range-note');
    this.rangeNote.setAttribute('aria-live', 'polite');
    wrap.appendChild(this.rangeNote);
    this.updateRangeNote();
    if (!maleOn && kind === 'button') {
      wrap.appendChild(el('p', 'allflex-field__hint allflex-mb-picker__button-note', 'Male tag: the button comes blank. Nothing to type.'));
    }

    if (maleOn) {
      var mBlock = this.tagBlock('Male tag', program.male, this.sel.male, 'Male Line ', false);
      var copy = el('button', 'allflex-mb-picker__copy-btn', 'Copy female wording');
      copy.type = 'button';
      var copyMsg = el('span', 'allflex-field__hint allflex-mb-picker__copy-msg');
      copy.addEventListener('click', function () {
        var r = copyFemaleToMale(program, self.sel.female, !!self.sel.allText);
        Object.keys(r.male).forEach(function (k) { self.sel.male[k] = r.male[k]; });
        self.renderBoxes();
        self.update();
        var msg = self.root.querySelector('.allflex-mb-picker__copy-msg');
        if (msg && r.skipped.length) {
          msg.textContent = 'Line ' + r.skipped.join(', ') + " didn't fit the male tag. Type it in yourself.";
        }
      });
      var head = mBlock.querySelector('.allflex-mb-picker__tag-heading');
      if (head) { head.appendChild(copy); head.appendChild(copyMsg); }
      wrap.appendChild(mBlock);
    }
  };

  Picker.prototype.tagBlock = function (heading, specs, entries, prefix, allText) {
    var self = this;
    var block = el('div', 'allflex-mb-picker__tag-block');
    if (heading) {
      var h = el('div', 'allflex-mb-picker__tag-heading');
      h.appendChild(el('strong', null, heading));
      block.appendChild(h);
    }
    specs.forEach(function (spec) {
      var key = prefix + spec.line;
      var entry = entries[spec.line] || (entries[spec.line] = {});
      var row = el('div', 'allflex-mb-picker__line');
      var baseId = self.id + '-' + key.replace(/\s+/g, '-').toLowerCase();
      var label = el('label', 'allflex-mb-picker__line-label');
      label.htmlFor = baseId;
      label.appendChild(el('span', 'allflex-mb-picker__line-name', 'Line ' + spec.line));
      label.appendChild(el('span', 'allflex-mb-picker__line-spec', boxHint(spec, allText)));
      row.appendChild(label);
      var inputs = el('div', 'allflex-mb-picker__line-inputs');
      var refs = {};
      if (isNumberLine(spec, allText)) {
        var start = self.textInput(baseId, spec.maxChars, 'Start no.', entry.start, true);
        var end = self.textInput(baseId + '-end', spec.maxChars, 'End no.', entry.end, true);
        start.setAttribute('aria-label', 'Line ' + spec.line + ' start number');
        end.setAttribute('aria-label', 'Line ' + spec.line + ' end number (optional)');
        var onNum = function () {
          var s = digitsOnly(start.value);
          var e = digitsOnly(end.value);
          if (s !== start.value) start.value = s;
          if (e !== end.value) end.value = e;
          entry.start = s;
          entry.end = e;
          self.update();
          if (prefix === 'Line ') self.syncQuantity();
        };
        start.addEventListener('input', onNum);
        end.addEventListener('input', onNum);
        inputs.appendChild(start);
        inputs.appendChild(el('span', 'allflex-mb-picker__to', 'to'));
        inputs.appendChild(end);
        refs.start = start;
        refs.end = end;
      } else {
        var t = self.textInput(baseId, spec.maxChars, 'Up to ' + spec.maxChars + ' characters', entry.text, false);
        t.addEventListener('input', function () { entry.text = t.value; self.update(); });
        inputs.appendChild(t);
        refs.text = t;
      }
      row.appendChild(inputs);
      var err = el('span', 'allflex-mb-picker__line-error');
      err.setAttribute('aria-live', 'polite');
      row.appendChild(err);
      refs.error = err;
      self.lineInputs[key] = refs;
      block.appendChild(row);
    });
    return block;
  };

  Picker.prototype.qtyInput = function () {
    return this.formId ? document.querySelector('.quantity__input[form="' + this.formId + '"]') : null;
  };

  /** Quantity follows the female number range (Aaron, 23 Oct): 1 to 30 -> 30. */
  Picker.prototype.syncQuantity = function () {
    var n = rangeQuantity(this.program(), this.sel.female, !!this.sel.allText);
    var q = this.qtyInput();
    if (n > 0 && q && String(q.value) !== String(n)) {
      q.value = n;
      q.dispatchEvent(new Event('input', { bubbles: true }));
      q.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.updateRangeNote();
  };

  /** "Your numbers make 30 tags." plus whether the quantity matches. Never blocks the order. */
  Picker.prototype.updateRangeNote = function () {
    var note = this.rangeNote;
    if (!note) return;
    var n = rangeQuantity(this.program(), this.sel.female, !!this.sel.allText);
    var q = this.qtyInput();
    var qty = q ? parseInt(q.value, 10) : NaN;
    var min = q ? parseInt(q.getAttribute('data-min') || q.getAttribute('min') || '1', 10) : 1;
    var text = '';
    if (n > 0) {
      text = 'Your numbers make ' + n + (n === 1 ? ' tag. ' : ' tags. ');
      if (q && qty !== n) text += 'Quantity is ' + (isNaN(qty) ? 'blank' : qty) + ', so check one or the other.';
      else if (n < min) text += 'The minimum order is ' + min + ' tags, so add more numbers.';
      else text += 'Quantity set to ' + n + '.';
    }
    note.textContent = text;
    note.hidden = !text;
  };

  Picker.prototype.textInput = function (id, max, placeholder, value, numeric) {
    var i = el('input', 'allflex-field__input allflex-mb-picker__input');
    i.type = 'text';
    i.id = id;
    i.maxLength = Number(max);
    i.placeholder = placeholder;
    i.autocomplete = 'off';
    i.spellcheck = false;
    if (numeric) {
      i.inputMode = 'numeric';
      i.pattern = '[0-9]*';
      i.className += ' allflex-mb-picker__input--number';
    }
    i.value = value || '';
    return i;
  };

  Picker.prototype.update = function () {
    if (!this.lookup) { this.writeOutputs(null); return; }
    var res = evaluate({
      page: this.lookup.page,
      program: this.program(),
      custom: this.sel.custom,
      customText: this.els.customText ? this.els.customText.value : '',
      female: this.sel.female,
      male: this.sel.male,
      allText: this.sel.allText,
      maleTagNeeded: this.maleRadios.length > 0,
      maleTagChosen: this.maleRadios.some(function (r) { return r.checked; })
    });
    this.writeOutputs(res);
    this.renderFeedback(res);
  };

  function setOut(node, value, spec) {
    if (!node) return false;
    var v = value || '';
    var changed = node.value !== v || node.disabled !== !v;
    node.value = v;
    node.disabled = !v;
    if (spec) node.setAttribute('data-spec', spec); else node.removeAttribute('data-spec');
    return changed;
  }

  /** Push the evaluation into the snippet's inputs. res = null clears everything. */
  Picker.prototype.writeOutputs = function (res) {
    var props = res ? res.props : emptyProps();
    var specs = res ? res.specs : {};
    var out = this.out;
    var std = this.root.querySelector('[data-mb-print-standard]');
    var feeChanged = [];
    Object.keys(out).forEach(function (name) {
      var node = out[name];
      if (name === 'Print Type') {
        // Hidden radio pair: Complex Print only for a custom layout, else Standard Print.
        var on = props['Print Type'] === 'Complex Print';
        if (node.checked !== on) {
          node.checked = on;
          if (std) std.checked = !on;
          feeChanged.push(on ? node : (std || node));
        }
        return;
      }
      var changed = setOut(node, props[name], props[name] ? specs[name] : '');
      if (changed && (name === 'Print Lines' || name === 'Male Print Lines')) feeChanged.push(node);
    });
    if (!res && this.els.customText) this.els.customText.disabled = true;
    feeChanged.forEach(function (node) { fire(node, 'change'); });

    var gate = this.els.gate;
    if (gate) {
      var g = res ? res.gate : '';
      if (gate.value !== g) { gate.value = g; fire(gate, 'change'); }
    }
    // Let tag-order-summary.js recompose Print Details from the fresh values.
    fire(out['Print Style'], 'change');
  };

  Picker.prototype.renderFeedback = function (res) {
    var self = this;
    Object.keys(this.lineInputs).forEach(function (key) {
      var refs = self.lineInputs[key];
      var msg = res.lineErrors[key] || '';
      refs.error.textContent = msg;
      [refs.text, refs.start, refs.end].forEach(function (i) {
        if (i) i.classList.toggle('allflex-mb-picker__input--error', !!msg);
      });
    });

    var sum = this.els.summary;
    if (sum) {
      sum.textContent = '';
      var program = this.program();
      if (program && !this.sel.custom) {
        sum.appendChild(el('strong', 'allflex-mb-picker__summary-title', 'Your tags will read'));
        var maleOn = hasPrintedMale(program);
        var addTag = function (title, specs, prefix) {
          var list = el('ul', 'allflex-mb-picker__summary-list');
          if (title) sum.appendChild(el('span', 'allflex-mb-picker__summary-tag', title));
          specs.forEach(function (spec) {
            var v = res.props[prefix + spec.line];
            list.appendChild(el('li', v ? null : 'is-blank', 'Line ' + spec.line + ': ' + (v || 'left blank')));
          });
          sum.appendChild(list);
        };
        addTag(maleOn ? 'Female (' + program.code + ')' : program.code, program.female, 'Line ');
        if (maleOn) addTag('Male (' + res.props['Male Print Style'] + ')', program.male, 'Male Line ');
        else if (program.maleButton) sum.appendChild(el('span', 'allflex-mb-picker__summary-tag', 'Male: button supplied blank'));
        if (res.filled === 0) sum.appendChild(el('p', 'allflex-field__hint', 'Type your wording above to see how your tags will read.'));
        sum.hidden = false;
      } else {
        sum.hidden = true;
      }
    }

    var probs = this.els.problems;
    if (probs) {
      probs.textContent = '';
      var shown = res.errors.filter(function (e) {
        return e !== 'Pick a layout' && e !== 'Type the wording for at least one line' && e !== 'Describe your custom design';
      });
      shown.forEach(function (e) { probs.appendChild(el('li', null, e)); });
      probs.hidden = !shown.length;
    }
  };

  // ===========================================================================
  // PAIR PHOTO COLOUR PREVIEW (Aaron, 23 Oct). The set pages' first photo is the pair in Orange
  // (file name allflex-pair-*, built by scripts/allflex-transition/pair-images/build.py). Tapping a
  // colour swatch recolours that photo in the browser with the same maths as build.py recolour_hex().
  // Photo only: the order, the properties and the price are untouched. Any failure leaves the orange.
  // ===========================================================================
  var PAIR_BASE_HUE = 0.075; // the orange the pair photos are built in (our real orange Large Male)

  function rgbToHsv(r, g, b) {
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn, h = 0;
    if (d > 0) {
      if (mx === r) h = ((g - b) / d) % 6;
      else if (mx === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h /= 6;
      if (h < 0) h += 1;
    }
    return [h, mx > 0 ? d / mx : 0, mx / 255];
  }

  function hsvToRgb(h, s, v) {
    var i = Math.floor(h * 6) % 6, f = h * 6 - Math.floor(h * 6);
    var p = v * (1 - s), q = v * (1 - f * s), t = v * (1 - (1 - f) * s);
    var c = [[v, t, p], [q, v, p], [p, v, t], [p, q, v], [t, p, v], [v, p, q]][i];
    return [c[0] * 255, c[1] * 255, c[2] * 255];
  }

  /** Recolour an orange pair photo's pixels (RGBA array, in place) to the hex the customer picked. */
  function recolourPixels(data, hex) {
    var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || ''));
    if (!m) return false;
    var t = rgbToHsv(parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16));
    for (var i = 0; i < data.length; i += 4) {
      var px = rgbToHsv(data[i], data[i + 1], data[i + 2]);
      var cover = Math.abs(px[0] - PAIR_BASE_HUE) < 0.06 ? Math.min(Math.max((px[1] - 0.25) / 0.7, 0), 1) : 0;
      if (!cover) continue; // white background, grey pin
      var n = hsvToRgb(t[0], t[1], Math.min(px[2] * t[2], 1));
      for (var k = 0; k < 3; k++) data[i + k] = Math.round(cover * n[k] + (1 - cover) * data[i + k]);
    }
    return true;
  }

  function cssColourToHex(c) {
    var m = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(String(c || ''));
    if (!m) return /^#[0-9a-f]{6}$/i.test(c) ? c : '';
    return '#' + [m[1], m[2], m[3]].map(function (x) { return ('0' + Number(x).toString(16)).slice(-2); }).join('');
  }

  function bootPairColour(doc) {
    var img = doc.querySelector('.product-slider__slide[data-slide-index="0"] img');
    if (!img || !/allflex-pair-/.test(img.getAttribute('src') || '')) return;
    var thumbBtn = doc.querySelector('.product-slider__thumbnail[data-thumbnail-index="0"]');
    var thumb = thumbBtn ? thumbBtn.querySelector('img') : null;
    var orig = { src: img.getAttribute('src'), srcset: img.getAttribute('srcset'), alt: img.getAttribute('alt') || '', thumb: thumb ? thumb.getAttribute('src') : null };
    var base = null;
    var cache = {};
    var loadBase = function () {
      if (!base) {
        base = new Promise(function (resolve, reject) {
          var im = new Image();
          im.crossOrigin = 'anonymous'; // cdn.shopify.com sends access-control-allow-origin: *
          im.onload = function () { resolve(im); };
          im.onerror = reject;
          im.src = orig.src + (orig.src.indexOf('?') < 0 ? '?' : '&') + 'mb-colour=1';
        });
      }
      return base;
    };
    var restore = function () {
      img.setAttribute('src', orig.src);
      if (orig.srcset) img.setAttribute('srcset', orig.srcset);
      img.setAttribute('alt', orig.alt);
      if (thumb && orig.thumb) thumb.setAttribute('src', orig.thumb);
    };
    var show = function (name, hex) {
      if (thumbBtn && !thumbBtn.classList.contains('active')) thumbBtn.click();
      if (/^orange$/i.test(name) || !hex) { restore(); return; } // the photo is already the real orange
      loadBase().then(function (im) {
        var key = hex.toLowerCase();
        if (!cache[key]) {
          var c = doc.createElement('canvas');
          c.width = im.naturalWidth;
          c.height = im.naturalHeight;
          var ctx = c.getContext('2d');
          ctx.drawImage(im, 0, 0);
          var d = ctx.getImageData(0, 0, c.width, c.height);
          if (!recolourPixels(d.data, hex)) return;
          ctx.putImageData(d, 0, 0);
          cache[key] = c.toDataURL('image/jpeg', 0.9);
        }
        img.removeAttribute('srcset');
        img.setAttribute('src', cache[key]);
        img.setAttribute('alt', orig.alt + ', shown in ' + name);
        if (thumb) thumb.setAttribute('src', cache[key]);
      }).catch(function () { restore(); });
    };
    doc.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.name !== 'properties[Color]' || !t.checked) return;
      var vis = t.parentNode ? t.parentNode.querySelector('.color-swatch__visual') : null;
      show(t.value, vis ? cssColourToHex(getComputedStyle(vis).backgroundColor) : '');
    });
  }

  function boot(doc) {
    var start = function () {
      if (!doc.__allflexPairColour) {
        doc.__allflexPairColour = true;
        try { bootPairColour(doc); } catch (e) { /* photo stays orange */ }
      }
      Array.prototype.forEach.call(doc.querySelectorAll('[data-allflex-mb-picker]'), function (root) {
        if (root.__allflexMbPicker) return;
        try {
          var p = new Picker(root);
          root.__allflexMbPicker = p;
          p.init();
        } catch (e) {
          // Fail closed on any surprise: the gate stays empty so add to cart stays blocked.
          var fail = root.querySelector('[data-mb-fail]');
          var status = root.querySelector('[data-mb-status]');
          var body = root.querySelector('[data-mb-body]');
          if (status) status.hidden = true;
          if (body) body.hidden = true;
          if (fail) fail.hidden = false;
        }
      });
    };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start);
    else start();
  }

  return {
    boot: boot,
    feeToken: feeToken,
    isValidBook: isValidBook,
    lookup: lookup,
    lineCounts: lineCounts,
    programsFor: programsFor,
    autoCode: autoCode,
    rangeQuantity: rangeQuantity,
    recolourPixels: recolourPixels,
    findProgram: findProgram,
    hasPrintedMale: hasPrintedMale,
    printedTagCount: printedTagCount,
    pageKind: pageKind,
    customAllowed: customAllowed,
    splitCode: splitCode,
    isNumberLine: isNumberLine,
    cardLineText: cardLineText,
    dataSpec: dataSpec,
    boxHint: boxHint,
    digitsOnly: digitsOnly,
    checkLine: checkLine,
    evaluate: evaluate,
    copyFemaleToMale: copyFemaleToMale,
    CUSTOM_STYLE: CUSTOM_STYLE
  };
});
